import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, KeyboardAvoidingView, Platform, ScrollView, Linking } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useAuth } from '../src/context/AuthContext';
import { useTheme } from '../src/context/ThemeContext';
import type { ColorTheme } from '../src/utils/theme';
import { useToast } from '../src/components/Toast';
import Button from '../src/components/Button';
import Input from '../src/components/Input';
import ContentContainer from '../src/components/ContentContainer';
import { useModalBottomPadding } from '../src/utils/useModalBottomPadding';
import { spacing, fontSize, fontWeight, borderRadius } from '../src/utils/theme';

/** LoginScreen - Email/account-number + password sign-in with 2FA and verification redirects. */
export default function LoginScreen() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const router = useRouter();
  const { returnTo } = useLocalSearchParams<{ returnTo?: string }>();
  const toast = useToast();
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const bottomPad = useModalBottomPadding();

  /** Authenticate and route based on verification/2FA requirements. */
  const handleLogin = async () => {
    if (!identifier || !password) { setError('Please fill in all fields'); return; }
    setError('');
    setLoading(true);
    try {
      // POST /api/auth/login — may return verification or 2FA redirect
      const res = await login(identifier, password);
      if (res.requiresVerification) {
        router.push({ pathname: '/verify-email', params: { email: res.email, returnTo: returnTo || '' } });
      } else if (res.requires2FA) {
        router.push({ pathname: '/verify-2fa', params: { email: res.email, returnTo: returnTo || '' } });
      } else {
        router.replace((returnTo || '/') as any);
      }
    } catch (err: any) { setError(err.message || 'Login failed'); }
    finally { setLoading(false); }
  };

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView contentContainerStyle={[styles.scroll, { paddingBottom: spacing.lg + bottomPad }]} keyboardShouldPersistTaps="handled">
        <ContentContainer maxWidth={480}>
          <Text style={styles.title}>Welcome back</Text>
          <Text style={styles.subtitle}>Sign in to your account</Text>

          <Input label="Email or Account Number" icon="person-outline" placeholder="Email or RP-XXXXXXXX" value={identifier} onChangeText={setIdentifier} autoCapitalize="none" keyboardType="email-address" returnKeyType="next" maxLength={255} />
          <Input label="Password" icon="lock-closed-outline" placeholder="Enter your password" value={password} onChangeText={setPassword} secureTextEntry autoCapitalize="none" autoCorrect={false} onSubmitEditing={handleLogin} returnKeyType="done" />
          <TouchableOpacity onPress={() => router.push('/forgot-password')} style={styles.forgotContainer}>
            <Text style={styles.forgotText}>Forgot password?</Text>
          </TouchableOpacity>
          {error ? <Text style={styles.error}>{error}</Text> : null}
          <Button title="Sign In" onPress={handleLogin} loading={loading} size="lg" style={styles.loginBtn} />

          <TouchableOpacity onPress={() => router.push({ pathname: '/register', params: { returnTo: returnTo || '' } })} style={styles.linkContainer}>
            <Text style={styles.linkText}>Don't have an account? <Text style={styles.link}>Create one</Text></Text>
          </TouchableOpacity>

          <TouchableOpacity onPress={() => router.push('/terms')} style={styles.tosContainer}>
            <Text style={styles.tosText}>Terms of Service</Text>
          </TouchableOpacity>

          <TouchableOpacity onPress={() => Linking.openURL('mailto:support@rinsepay.app')} style={styles.supportContainer}>
            <Text style={styles.supportText}>support@rinsepay.app</Text>
          </TouchableOpacity>
        </ContentContainer>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scroll: { flexGrow: 1, padding: spacing.lg, paddingTop: spacing.xl },
  title: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.xs },
  subtitle: { fontSize: fontSize.md, color: c.textSecondary, marginBottom: spacing.lg },
  error: { fontSize: fontSize.sm, color: c.danger, backgroundColor: c.danger + '10', padding: spacing.md, borderRadius: borderRadius.sm, marginBottom: spacing.md, textAlign: 'center' },
  forgotContainer: { alignItems: 'flex-end', marginTop: spacing.xs, marginBottom: spacing.xs },
  forgotText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
  loginBtn: { marginTop: spacing.sm },
  linkContainer: { alignItems: 'center', marginTop: spacing.lg },
  linkText: { fontSize: fontSize.sm, color: c.textSecondary },
  link: { color: c.primary, fontWeight: fontWeight.semibold },
  tosContainer: { alignItems: 'center', marginTop: spacing.md },
  tosText: { fontSize: fontSize.xs, color: c.textLight },
  supportContainer: { alignItems: 'center', marginTop: spacing.sm },
  supportText: { fontSize: fontSize.xs, color: c.primary },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
